"use client";

import { PermissionEnums } from "@grudder/constants/permissions";
import { RolePermissions } from "@grudder/constants/permissions";
import { RootState } from "@grudder/redux/store";
import { IUserPermission } from "@grudder/types/user-permission.types";
import { IUserRole } from "@grudder/types/user.type";
import { getSession } from "next-auth/react";
import React from "react";
import { createContext, useContext, ReactNode, useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { userRoles } from "@grudder/lib/const";

type UserContextType = {
  hasPemission: (permission: PermissionEnums | string) => boolean;
  compareUserRoles: (userRole: IUserRole) => number;
  organizationRole: string;
  userPermissions: IUserPermission[];
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export const PermissionProvider = ({ children }: { children: ReactNode }) => {
  const { activeOrganization } = useSelector((state: RootState) => state.organization);
  const { userPermissions, loading } = useSelector((state: RootState) => state.permissions as any);
  const { allPermissions } = useSelector((state: RootState) => state.permissions);
  const organizationRole = activeOrganization?.data?.role?.name || "";

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    const getUserId = async () => {
      try {
        const session: any = await getSession();
        const userId = session?.user?.id;
        console.log("Session data:", { session, userId });
        setCurrentUserId(userId || null);
      } catch (error) {
        console.error("Failed to get session:", error);
        setCurrentUserId(null);
      }
    };

    getUserId();
  }, []);

  const hasPemission = useMemo(() => {
    const permissions = RolePermissions[organizationRole] ?? [];

    if (loading) return () => false; // Return a function that always returns false while loading

    return (permission: PermissionEnums | string): boolean => {
      if (!permission) return true;

      // If no current user ID, deny access
      if (!currentUserId) {
        return false;
      }

      // Support sub-permissions for individual custom modules: "module-<moduleId>"
      if (typeof permission === "string" && permission.startsWith("module-")) {
        const moduleId = permission.replace("module-", "");

        // Helper to compare user entries that may be objects or strings
        const isSameUser = (user: any) => {
          if (!user) return false;
          return typeof user === "object" ? user._id === currentUserId : user === currentUserId;
        };

        // Map role name -> id for comparison
        const roleIdMapped = ((): string | null => {
          const found = userRoles.find((r) => r.label.toUpperCase() === organizationRole.toUpperCase());
          return found?.id || null;
        })();

        // Determine if parent ManageCustomModule is allowed for current user
        const parentDbPerm: IUserPermission | undefined =
          userPermissions.find((p: IUserPermission) => p.featureName === PermissionEnums.ManageCustomModule) || allPermissions.find((p: IUserPermission) => p.featureName === PermissionEnums.ManageCustomModule);

        const parentAllowed = (() => {
          if (parentDbPerm) {
            // Explicit user deny takes highest precedence
            if (parentDbPerm.notAllowedUsers?.some(isSameUser)) return false;
            // Explicit user allow
            if (parentDbPerm.allowedUsers?.some(isSameUser)) return true;
            // Role-based allow (compare by name or id inside object or string)
            const allowedByRole = parentDbPerm.allowedUserTypes?.some((userType: any) => {
              if (typeof userType === "object") {
                return userType.name === organizationRole || (!!roleIdMapped && (userType._id === roleIdMapped || userType.id === roleIdMapped));
              }
              // userType is string (could be id or name)
              return userType === organizationRole || (!!roleIdMapped && userType === roleIdMapped);
            });
            return !!allowedByRole;
          }
          // Fallback to hardcoded role permissions
          return permissions.includes(PermissionEnums.ManageCustomModule);
        })();

        if (!parentAllowed) return false;

        // If module-level attributes exist, evaluate them
        const moduleAttrs = parentDbPerm?.attributes?.[moduleId];
        if (moduleAttrs && typeof moduleAttrs === "object") {
          const { allowedUsers = [], notAllowedUsers = [], allowedUserTypes = [] } = moduleAttrs as any;

          // Explicit deny at module level
          if ((notAllowedUsers as any[]).some(isSameUser)) return false;
          // Explicit allow at module level
          if ((allowedUsers as any[]).some(isSameUser)) return true;
          // Role-based allow at module level
          const allowedByRole = (allowedUserTypes as any[]).some((ut: any) => {
            if (typeof ut === "object") {
              return ut.name === organizationRole || (!!roleIdMapped && (ut._id === roleIdMapped || ut.id === roleIdMapped));
            }
            // string id or name
            return ut === organizationRole || (!!roleIdMapped && ut === roleIdMapped);
          });
          return !!allowedByRole; // If attributes present and no match, deny by default
        }

        // No module-specific attributes set: inherit parent allow
        return true;
      }

      if (userPermissions?.length) {
        const isInDbPermissions: IUserPermission | undefined = userPermissions.find((p: IUserPermission) => p.featureName === (permission as string)) || allPermissions.find((p: IUserPermission) => p.featureName === (permission as string));

        if (isInDbPermissions) {
          // STEP 1: Check if current user is in notAllowedUsers (highest precedence - deny)
          const isInNotAllowedUsers =
            isInDbPermissions.notAllowedUsers?.some((user: any) => {
              if (typeof user === "object") {
                return user._id === currentUserId;
              }
              return user === currentUserId;
            }) || false;

          if (isInNotAllowedUsers) {
            console.log(`User ${currentUserId} denied by notAllowedUsers for ${permission}`);
            return false; // Explicitly denied
          }

          // STEP 2: Check if current user is in allowedUsers (second precedence - allow)
          const isInAllowedUsers =
            isInDbPermissions.allowedUsers?.some((user: any) => {
              if (typeof user === "object") {
                return user._id === currentUserId;
              }
              return user === currentUserId;
            }) || false;

          if (isInAllowedUsers) {
            console.log(`User ${currentUserId} allowed by allowedUsers for ${permission}`);
            return true; // Explicitly allowed
          }

          // STEP 3: Check role-based permission from DB
          const hasRolePermission =
            isInDbPermissions.allowedUserTypes?.some((userType: any) => {
              if (typeof userType === "object") {
                return userType.name === organizationRole;
              }
              return userType === organizationRole;
            }) || false;

          if (hasRolePermission) {
            // console.log(`User ${currentUserId} allowed by role ${organizationRole} from DB for ${permission}`);
            return true;
          }

          console.log(`User ${currentUserId} denied - role ${organizationRole} not in allowedUserTypes for ${permission}`);
          return false; // Role not allowed in DB
        } else {
          // No DB permission found, fall back to hardcoded role permissions
          const hasHardcodedPermission = permissions.includes(permission as PermissionEnums);
          //console.log(`User ${currentUserId} fallback to hardcoded role ${organizationRole}: ${hasHardcodedPermission} for ${permission}`);
          return hasHardcodedPermission;
        }
      }

      // Final fallback to hardcoded role permissions
      const hasHardcodedPermission = permissions.includes(permission as PermissionEnums);
      console.log(`User ${currentUserId} final fallback to hardcoded role ${organizationRole}: ${hasHardcodedPermission} for ${permission}`);
      return hasHardcodedPermission;
    };
  }, [userPermissions, allPermissions, organizationRole, currentUserId, loading]);

  const compareUserRoles = useMemo(() => {
    return (userRole: IUserRole): number => {
      return activeOrganization?.data?.role?.weightAge > userRole?.weightAge ? 1 : -1;
    };
  }, [activeOrganization]);

  return <UserContext.Provider value={{ hasPemission, compareUserRoles, userPermissions, organizationRole }}>{children}</UserContext.Provider>;
};

export const usePermission = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within UserProvider");
  return context;
};
